import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, Card, CardContent, Button, CircularProgress, Alert, Grid, Chip } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import RequireAuth from '../../components/athletes/RequireAuth';
import { useAuth } from '../../context/AuthContext';
import { listMyProgramMemberships, listProgramSlugsByIds } from '../../services/programService';
import { programRoleLabel, programRoleChipStyle } from '../../utils/programRoles';

function ProgramsInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [programs, setPrograms] = useState([]);
  // Phase C.2b — Program-id -> slug map, fetched separately since
  // list_my_program_memberships() (025) doesn't return slug (see
  // listProgramSlugsByIds()'s own comment for why this is a plain
  // client-side lookup, not a new RPC/migration). Absent entries mean
  // "no public slug yet" — the card renders no public-page action for
  // those, never a dead link.
  const [programSlugs, setProgramSlugs] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Captured ONCE from the navigation state ManageProgramPage passed in
  // (lazy initializer — runs only on this component's first render),
  // never re-read from location.state directly for display. This is
  // what lets the banner render correctly on the one intended pass
  // while being fully immune to the state-clearing navigate() below —
  // without it, clearing location.state in the same tick the banner is
  // meant to display would make the banner disappear before it's ever
  // seen.
  const [relinquishedProgram] = useState(() => location.state?.relinquished ?? null);
  // Set by ManageProgramPage after a completed ownership transfer where
  // the outgoing Owner chose NOT to retain Program Admin access — same
  // capture-once pattern as relinquishedProgram above, distinct wording
  // since "transferred away" and "relinquished my own admin role" are
  // different facts worth telling apart.
  const [transferredProgram] = useState(() => location.state?.transferredProgram ?? null);

  useEffect(() => {
    // Consumes BOTH one-time navigation states in one pass — see
    // relinquishedProgram's own comment above for the full reasoning
    // (history.state survives hard refresh/back-forward; a `replace`
    // navigation to the same URL with empty state prevents either from
    // resurrecting). Self-terminating: the replace call clears
    // location.state, re-running this effect once more with both guards
    // false.
    if (location.state?.relinquished || location.state?.transferredProgram) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    // Guards on currentUser and depends on it, mirroring MyTeamsPage's
    // already-established pattern — RequireAuth alone gates whether this
    // component mounts at all, but does not guarantee this effect can
    // never fire before the authenticated session is fully ready for an
    // outgoing .rpc() call (e.g. on a hard refresh, where auth state can
    // resolve in more than one step — see AuthContext's own extensive
    // comments on this exact class of race). Without this, an effect
    // with an empty dependency array fires exactly once and has no way
    // to self-correct if that first firing was premature; listMyPrograms()
    // would then return zero rows for a real, active Program Admin,
    // which must never be shown as "no Programs" — only as still
    // loading. Depending on currentUser means a later, correct auth
    // state update naturally re-triggers a fresh, correct fetch.
    if (!currentUser) return;
    listMyProgramMemberships()
      .then(async (rows) => {
        setPrograms(rows);
        // Best-effort — a slug-lookup failure should never block the
        // primary "manage your programs" experience; it just means no
        // "View Public Page" action renders for anyone this pass.
        try {
          setProgramSlugs(await listProgramSlugsByIds(rows.map(p => p.id)));
        } catch (_) {
          setProgramSlugs({});
        }
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [currentUser]);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 900 }}>My Programs</Typography>
        {/* Phase C.1 — the first entry point to create_program_with_owner()
            (036). Always shown, not gated on anything, mirroring "a
            genuinely new organization must be creatable without needing
            anyone's prior permission" (020's own reasoning for
            create_program_with_admin, unchanged for its Owner-model
            successor). */}
        <Button
          variant="contained" color="primary"
          onClick={() => navigate('/my-programs/new')}
          sx={{ textTransform: 'none', fontWeight: 800, px: 2.5 }}
        >
          + Create Program
        </Button>
      </Box>

      {relinquishedProgram && (
        <Alert severity="success" sx={{ mb: 3 }}>
          You are no longer a Program Admin for {relinquishedProgram}.
        </Alert>
      )}
      {transferredProgram && (
        <Alert severity="success" sx={{ mb: 3 }}>
          You transferred ownership of {transferredProgram}. You no longer have Program-level access to it unless you hold another Program relationship.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>
      ) : programs.length === 0 ? (
        <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography sx={{ fontSize: '2.2rem', mb: 1.5 }}>🏟️</Typography>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>No Programs yet</Typography>
            <Typography sx={{ color: '#9eb8d0' }}>
              You don't administer any Programs yet.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {programs.map(p => (
            <Grid item xs={12} sm={6} key={p.id}>
              <Card
                onClick={() => navigate(`/my-programs/${p.id}`)}
                sx={{ cursor: 'pointer', backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a', height: '100%', '&:hover': { borderColor: 'rgba(46,168,75,0.4)' } }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>{p.name}</Typography>
                    <Chip size="small" label={programRoleLabel(p.viewer_role)} sx={{ fontWeight: 700, ...programRoleChipStyle(p.viewer_role) }} />
                  </Box>
                  {(p.primary_city || p.primary_state_code) && (
                    <Typography sx={{ color: '#6a8aa8', fontSize: '0.8rem', mt: 0.25 }}>
                      {[p.primary_city, p.primary_state_code].filter(Boolean).join(', ')}
                    </Typography>
                  )}
                  <Typography sx={{ color: '#9eb8d0', fontSize: '0.85rem', mt: 1 }}>
                    {p.season_team_count} Season Team{Number(p.season_team_count) === 1 ? '' : 's'}
                  </Typography>
                  {/* Phase C.2b — two explicit destinations instead of one
                      ambiguous "Open Program". "Manage Program" stays the
                      PRIMARY action (bolder, matches the Card's own
                      onClick target exactly) since this is still the
                      authenticated management page; "View Public Page" is
                      a visually secondary/subtle action, and only renders
                      at all when this Program actually has a slug (see
                      programSlugs' own comment) — never a dead public
                      link. stopPropagation keeps a public-page click from
                      also triggering the Card's own onClick navigation
                      into management. */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
                    <Button size="small" sx={{ textTransform: 'none', fontWeight: 700, pl: 0 }}>
                      Manage Program →
                    </Button>
                    {programSlugs[p.id] && (
                      <Button
                        size="small"
                        onClick={e => { e.stopPropagation(); navigate(`/programs/${programSlugs[p.id]}`); }}
                        sx={{ textTransform: 'none', fontWeight: 500, fontSize: '0.78rem', color: '#6a8aa8', pl: 0 }}
                      >
                        View Public Page ↗
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
}

export default function MyProgramsPage() {
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      {/* Phase C.1a — the FFN landing page's "Find Your Program" CTA
          navigates here unconditionally; same cold-traffic copy as
          CreateProgramPage.js's own RequireAuth usage. */}
      <RequireAuth offerCreateAccount message="Sign in to find and manage your Programs.">
        <ProgramsInner />
      </RequireAuth>
    </Box>
  );
}
